import Vue from 'vue'
import { Widget, WidgetRow } from '..'
import { EventService } from '../../../../base/services/EventService'
import { PermutatorService } from '../permutator'
import { WidgetHeightType } from '../widget/WidgetTypes'
import { ResizeUnit, RowHeightSet } from './ResizeTypes'

class ResizeServiceProvider {
  protected nodesWithHeightTypeRemaining: { [containerUUID: string]: HTMLElement[] } = {}
  protected isBatchOperationInProgress = false

  constructor() {
    const cb = this.onResize.bind(this)
    window.addEventListener('resize', cb)
    EventService.$on('drag-drop-service-end-drag', cb)

    // init all widgets after the browser finished loading the page
    const readyStateCheckInterval = setInterval(() => {
      if (document.readyState === 'complete') {
        clearInterval(readyStateCheckInterval)
        this.processAllWidgets()
      }
    }, 10)
  }

  protected onResize(event) {
    Vue.nextTick(() => this.processAllWidgets())
  }

  protected processAllWidgets(container?) {
    this.isBatchOperationInProgress = true
    const nodes = (container || document).querySelectorAll('[data-widget-type]')

    nodes.forEach((node) => {
      const widget = PermutatorService.getByUUID(node.getAttribute('data-wbuuid'))

      if (!widget) return

      this.processWidget(node as HTMLElement, widget as Widget)
    })

    this.isBatchOperationInProgress = false
  }

  protected parseFloat(str: string): number {
    // filter all chars except numbers, dot and comma
    let result = str.replace(/[^0-9.,]+/g, '')

    // turn commas into dots
    result = result.replace(',', '.')

    // remove all dots except the last one
    let parts = result.split('.').filter((val) => val !== '')

    if (parts.length > 1) {
      result = parts.slice(0, -1).join('') + '.' + parts.slice(-1)
    }

    return parseFloat(result)
  }

  protected getWidgetContainerElement(widgetUUID: string): HTMLElement {
    const containerWidget = PermutatorService.getWidgetContainer(widgetUUID)

    return this.getNodeByUUID(containerWidget.wbUUID!)
  }

  protected processTypeGrid(node: HTMLElement, widget: Widget) {
    const colSize = node.offsetWidth / widget.size!
    const height = widget.height! || widget.minHeight! || 1

    node.style.height = (colSize * height) + 'px'
  }

  protected processTypePercentage(node: HTMLElement, widget: Widget) {
    const percentage = this.parseFloat(String(widget.height!))
    const container = this.getWidgetContainerElement(widget.wbUUID!)

    if (!container) return

    node.style.height = ((container.offsetHeight * percentage) / 100) + 'px'
  }

  protected addHeightTypeRemainingNode(containerUUID: string, node: HTMLElement) {
    if (!this.nodesWithHeightTypeRemaining[containerUUID]) {
      this.nodesWithHeightTypeRemaining[containerUUID] = []
    }

    if (this.nodesWithHeightTypeRemaining[containerUUID].indexOf(node) === -1) {
      this.nodesWithHeightTypeRemaining[containerUUID].push(node)
    }
  }

  protected removeHeightTypeRemainingNode(containerUUID: string, node: HTMLElement) {
    if (!this.nodesWithHeightTypeRemaining[containerUUID]) return

    const nodeIndex = this.nodesWithHeightTypeRemaining[containerUUID].indexOf(node)

    if (nodeIndex === -1) return

    this.nodesWithHeightTypeRemaining[containerUUID].splice(nodeIndex, 1)
  }

  protected resetNodesInContainer(containerUUID: string): HTMLElement[] {
    if (!this.nodesWithHeightTypeRemaining[containerUUID]) {
      return []
    }

    const nodes = this.nodesWithHeightTypeRemaining[containerUUID]

    nodes.forEach((node) => node.style.height = '')

    return nodes
  }

  protected processHeightTypeRemainingNode(node: HTMLElement, containerSpacePerNode: number, containerRowsHeightInfo: RowHeightSet): boolean {
    const widgetUUID = node.getAttribute('data-wbuuid')

    if (!widgetUUID) return false

    const widget = PermutatorService.getByUUID(widgetUUID) as Widget

    if (!widget) return false

    // check if widget is still relevant
    if (widget.heightType !== WidgetHeightType.remaining) return false

    const row = PermutatorService.getParentRow(widgetUUID)

    if (!row) return false

    const nodeHeight = node.offsetHeight

    let availableHeight = 0

    // if there's more than 1 item in the row first make sure we fill up all the row's height
    if (containerRowsHeightInfo[row.wbUUID!]) {
      availableHeight = containerRowsHeightInfo[row.wbUUID!] - nodeHeight
    }

    if (containerSpacePerNode > 0) {
      availableHeight += containerSpacePerNode
    }

    node.style.height = (nodeHeight + availableHeight) + 'px'

    return true
  }

  protected evaluateRowsInNodeList(nodeList: HTMLElement[]): RowHeightSet {
    const rows: RowHeightSet = {}

    nodeList.forEach((node) => {
      const widgetUUID = node.getAttribute('data-wbuuid')

      if (!widgetUUID) return

      const row = PermutatorService.getParentRow(widgetUUID)

      if (!row) return

      const rowUUID = row.wbUUID!

      if (rows[rowUUID]) return

      const rowNode = this.getNodeByUUID(rowUUID)

      if (!rowNode) return

      rows[rowUUID] = rowNode.offsetHeight
    })

    return rows
  }

  protected processHeightTypeRemainingInContainer(containerUUID: string) {
    // reset styling for nodes with height-type "remaining" in the container
    const containerNodes = this.resetNodesInContainer(containerUUID)

    if (!containerNodes.length) return

    // store height information for affected rows
    const rowsHeightInfo = this.evaluateRowsInNodeList(containerNodes)
    let numRows = 0

    // count rows
    for (let prop in rowsHeightInfo) {
      if (!rowsHeightInfo.hasOwnProperty(prop)) {
        continue
      }

      numRows++
    }

    // check if there's height available within the container
    const containerNode = this.getNodeByUUID(containerUUID)
    const remainingContainerHeight = this.getRemainingContainerHeight(containerNode)

    // equally share remaining space between rows
    const containerSpacePerNode = remainingContainerHeight / numRows

    containerNodes.forEach((node) => {
      const success = this.processHeightTypeRemainingNode(node, containerSpacePerNode, rowsHeightInfo)
      if (!success) {
        this.removeHeightTypeRemainingNode(containerUUID, node)
      }
    })
  }

  protected processTypeRemaining(node: HTMLElement, widget: Widget) {
    const container = PermutatorService.getWidgetContainer(widget.wbUUID!)
    const containerUUID = container.wbUUID!

    // add node to the list of nodes with height-type "remaining"
    this.addHeightTypeRemainingNode(containerUUID, node)

    this.processHeightTypeRemainingInContainer(containerUUID)
  }

  protected processTypePixel(node: HTMLElement, widget: Widget) {
    node.style.height = widget.height + 'px'
  }

  protected getInitialValueForGrid(node: HTMLElement, widget: Widget) {
    const nodeHeight = node.offsetHeight
    const colSize = node.offsetWidth / widget.size!

    const numColsRaw = nodeHeight / colSize
    let numCols = Math.floor(numColsRaw)

    // if we cannot translate the old height 1:1 into a grid value we need to check if there's enough space left in
    // the container to make the widget a little larger than before. Otherwise we leave it a little smaller
    if (numCols !== numColsRaw) {
      const isRootContainer = PermutatorService.isRootContainer(PermutatorService.getWidgetContainer(widget.wbUUID!).wbUUID!)
      let canIncrease = isRootContainer

      if (!isRootContainer) {
        const potentialHeightGain = (colSize * (numCols + 1)) - nodeHeight
        const remainingHeight = this.getMaxVerticalDragDistance(widget, ResizeUnit.pixel)
        canIncrease = remainingHeight >= potentialHeightGain
      }

      // enlarge widget if it's either part of a root container or there's enough space left in the container
      if (canIncrease) {
        numCols++
      }
    }

    // returning a value thats smaller than the widget's minimal height might result in an infinite loop causing the
    // app to crash. that's because SettingsPanel will try to force the minHeight on the widget, causing the widget
    // to update, causing this function to get called again, causing SettingsPanel to force the minHeight, ...
    if (widget.minHeight && (widget.minHeight > numCols)) {
      numCols = widget.minHeight
    }

    return numCols
  }

  protected getInitialValueForPercentage(node: HTMLElement, widget: Widget) {
    const container = this.getWidgetContainerElement(widget.wbUUID!)
    return (node.offsetHeight * 100) / container.offsetHeight
  }

  protected getInitialValueForPixel(node: HTMLElement, widget: Widget) {
    return node.offsetHeight
  }

  getWidgetMinSize(widget: Widget): number {
    // if it's not a container we can simply use the predefined settings
    if (!widget.rows) {
      return widget.minSize || 1
    }

    // if it's a container we assume it's not shrinkable and use its actual size
    return widget.size || 1
  }

  getNodeByUUID(wbUUID: string): HTMLElement {
    return document.querySelector('[data-wbuuid="' + wbUUID + '"]') as HTMLElement
  }

  getColSizeForWidget(widget: Widget): number {
    const containerElement = this.getNodeByUUID(widget.wbUUID!)

    if (!containerElement) {
      return -1
    }

    if (!widget.size!) {
      return -1
    }

    return containerElement.offsetWidth / widget.size!
  }

  getMaxVerticalDragDistance(widget: Widget, resizeUnit: ResizeUnit): number {
    const containerWidget = PermutatorService.getWidgetContainer(widget.wbUUID!)
    const containerUUID = containerWidget.wbUUID!
    const widgetUUID = widget.wbUUID!

    // if we are inside the root container or if height type is automatic we have no height limits
    if (PermutatorService.isRootContainer(containerUUID) || (containerWidget.heightType === WidgetHeightType.automatic)) {
      return -1
    }

    const row = PermutatorService.getParentRow(widgetUUID)

    if (!row) {
      return 0
    }

    // reset nodes with height type "remaining" to see how much space can be dynamically allocated
    this.resetNodesInContainer(containerUUID)
    let availableHeight = 0

    // if there's more than 1 item in the row, it might be that the widget has still space left to meet up the row's height
    // because other items in the row might be higher
    if (row.widgets.length > 1) {
      const rowNode = this.getNodeByUUID(row.wbUUID!)
      let rowHeight = 0

      if (rowNode) {
        rowHeight = rowNode.offsetHeight
      }

      const node = this.getNodeByUUID(widgetUUID)
      let nodeHeight = 0

      if (node) {
        nodeHeight = node.offsetHeight
      }

      availableHeight = rowHeight - nodeHeight
    }

    const containerNode = this.getWidgetContainerElement(widgetUUID)
    const remainingContainerHeight = this.getRemainingContainerHeight(containerNode)

    // height calculations done so we restore nodes with height type "remaining"
    this.processHeightTypeRemainingInContainer(containerUUID)

    // any space left in the container?
    if (remainingContainerHeight > 0) {
      availableHeight += remainingContainerHeight
    }

    // convert to grids?
    if (resizeUnit === ResizeUnit.grid) {
      availableHeight = availableHeight / this.getColSizeForWidget(widget)
    }

    // round off decimals to avoid being larger than the remaining height
    return Math.floor(availableHeight)
  }

  getRemainingContainerHeight(containerNode: HTMLElement): number {
    if (!containerNode) {
      return -1
    }

    const availableHeight = containerNode.offsetHeight

    const rows = containerNode.querySelectorAll('.widget--row')

    if (!rows.length) {
      return -1
    }

    const first = rows[0].getBoundingClientRect()
    const last = rows[rows.length - 1].getBoundingClientRect()

    return availableHeight - (Math.abs(last.top - first.top) + last.height)
  }

  getMinHeightForRowInPixels(row: WidgetRow): number {
    const widgets = row.widgets

    const numWidgets = widgets ? widgets.length : 0
    let i = 0
    let rowMinHeight = 0

    // loop through row contents and evaluate min height (in pixels)
    for (; i < numWidgets; i++) {
      const widget = widgets[i]

      let initialNodeHeight

      const node = this.getNodeByUUID(widget.wbUUID!)

      if (widget.heightType === WidgetHeightType.remaining) {
        initialNodeHeight = node.style.height
        node.style.height = ''
      }

      const widgetHeight = node.offsetHeight

      if (widget.heightType === WidgetHeightType.remaining) {
        node.style.height = initialNodeHeight
      }

      if (widgetHeight > rowMinHeight) {
        rowMinHeight = widgetHeight
      }
    }

    const rowNode = this.getNodeByUUID(row.wbUUID!)

    if (!rowMinHeight) {
      rowMinHeight = rowNode.offsetHeight
    }

    const style: CSSStyleDeclaration = (rowNode as any).currentStyle || window.getComputedStyle(rowNode)

    return rowMinHeight + this.parseFloat(style.marginTop || '0')
  }

  getMinHeightForContainer(widget: Widget, heightType: WidgetHeightType): number {
    const rows = widget.rows

    if (!rows) {
      return this.getMinHeightForWidget(widget, heightType)
    }

    let i = 0
    let totalHeight = 0
    const numRows = rows.length

    for (; i < numRows; i++) {
      totalHeight += this.getMinHeightForRowInPixels(rows[i])
    }

    // return pixels?
    if (heightType === WidgetHeightType.pixel) {
      return totalHeight
    }

    // return grid?
    if (heightType === WidgetHeightType.grid) {
      const colSize = this.getColSizeForWidget(widget)
      let remainder = totalHeight % colSize
      totalHeight = Math.floor(totalHeight / colSize)

      if (remainder > 0) {
        totalHeight++
      }

      return totalHeight
    }

    // return percent
    const container = this.getWidgetContainerElement(widget.wbUUID!)
    return (totalHeight * 100) / container.offsetHeight
  }

  getMinHeightForWidget(widget: Widget, heightType: WidgetHeightType): number {
    if (widget.rows) {
      return this.getMinHeightForContainer(widget, heightType)
    }

    const minHeightGrid = widget.minHeight! || 1

    // return grid value
    if (heightType === WidgetHeightType.grid) {
      return minHeightGrid
    }

    const pixelHeight = minHeightGrid * this.getColSizeForWidget(widget)

    // return percentage value
    if (heightType === WidgetHeightType.percentage) {
      const container = this.getWidgetContainerElement(widget.wbUUID!)
      return (pixelHeight * 100) / container.offsetHeight
    }

    // return pixel value
    return pixelHeight
  }

  getMaxValidHeightForWidget(widget: Widget): number {
    if (!widget || PermutatorService.isRootContainer(PermutatorService.getWidgetContainer(widget.wbUUID!).wbUUID!)) {
      return -1
    }

    let maxValidHeight = -1

    switch (widget.heightType) {
    case WidgetHeightType.grid:
      maxValidHeight = this.getMaxVerticalDragDistance(widget, ResizeUnit.grid)
      break
    case WidgetHeightType.pixel:
      maxValidHeight = this.getMaxVerticalDragDistance(widget, ResizeUnit.pixel)
      break
    default:
      // do nothing
      break
    }

    if (maxValidHeight > -1) {
      maxValidHeight += parseInt(String(widget.height!), 10)
    }

    return maxValidHeight
  }

  getInitialHeightTypeValue(widget: Widget): number {
    const node = this.getNodeByUUID(widget.wbUUID!)

    if (!node) {
      return 1
    }

    let initialHeightValue

    switch (widget.heightType) {
    case WidgetHeightType.grid:
      initialHeightValue = this.getInitialValueForGrid(node, widget)
      break
    case WidgetHeightType.percentage:
      initialHeightValue = this.getInitialValueForPercentage(node, widget)
      break
    case WidgetHeightType.pixel:
      initialHeightValue = this.getInitialValueForPixel(node, widget)
      break
    default:
      initialHeightValue = 1
      break
    }

    return initialHeightValue || 1
  }

  processWidget(node: HTMLElement, widget: Widget) {
    const heightType = widget.heightType!

    switch (heightType) {
    case WidgetHeightType.grid:
      this.processTypeGrid(node, widget)
      break
    case WidgetHeightType.percentage:
      this.processTypePercentage(node, widget)
      break
    case WidgetHeightType.remaining:
      this.processTypeRemaining(node, widget)
      break
    case WidgetHeightType.pixel:
      this.processTypePixel(node, widget)
      break
    default:
      // unsupported height type or height type that doesn't need a fixed height
      node.style.height = ''
      break
    }

    if (!this.isBatchOperationInProgress) {
      // update items of height-type "remaining" that are in the same container as the widget we just processed
      this.processHeightTypeRemainingInContainer(PermutatorService.getWidgetContainer(widget.wbUUID!).wbUUID!)

      // container element?
      if (widget.rows) {
        this.processAllWidgets(node)
      }
    }
  }
}

export const ResizeService = new ResizeServiceProvider()
